import { useState, useEffect } from 'react';

export type InAppBrowserType =
  | 'LINE'
  | 'Instagram'
  | 'X'
  | 'Facebook'
  | 'TikTok'
  | 'Threads'
  | 'Messenger'
  | 'Google'
  | 'Bing'
  | 'YouTube'
  | null;

interface UseInAppBrowserDetectionResult {
  /** アプリ内ブラウザかどうか */
  isInAppBrowser: boolean;
  /** 検出されたアプリ内ブラウザの種類 */
  browserType: InAppBrowserType;
  /** 検出処理が完了したかどうか */
  isDetectionComplete: boolean;
}

/**
 * アプリ内ブラウザを検出するカスタムフック
 *
 * LINE、Instagram、X(Twitter)、Facebookのアプリ内ブラウザを検出します。
 * SSR時は検出処理をスキップし、クライアントサイドでのみ実行されます。
 */
export const useInAppBrowserDetection = (): UseInAppBrowserDetectionResult => {
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [browserType, setBrowserType] = useState<InAppBrowserType>(null);
  const [isDetectionComplete, setIsDetectionComplete] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const ua = window.navigator.userAgent.toLowerCase();

    // LINE
    if (ua.includes('line')) {
      setIsInAppBrowser(true);
      setBrowserType('LINE');
    }
    // Instagram
    else if (ua.includes('instagram')) {
      setIsInAppBrowser(true);
      setBrowserType('Instagram');
    }
    // X (Twitter)
    else if (ua.includes('twitter')) {
      setIsInAppBrowser(true);
      setBrowserType('X');
    }
    // Facebook
    else if (ua.includes('fbav') || ua.includes('fban')) {
      setIsInAppBrowser(true);
      setBrowserType('Facebook');
    }
    // TikTok
    else if (
      ua.includes('tiktok') ||
      ua.includes('musical_ly') ||
      ua.includes('bytedancewebview')
    ) {
      setIsInAppBrowser(true);
      setBrowserType('TikTok');
    }
    // Threads (Instagram's Threads app, codename: Barcelona)
    else if (ua.includes('barcelona')) {
      setIsInAppBrowser(true);
      setBrowserType('Threads');
    }
    // Messenger
    else if (ua.includes('messenger')) {
      setIsInAppBrowser(true);
      setBrowserType('Messenger');
    }
    // Google App (GSA = Google Search App)
    else if (ua.includes('gsa/')) {
      setIsInAppBrowser(true);
      setBrowserType('Google');
    }
    // Bing App
    else if (ua.includes('bingsapphire/')) {
      setIsInAppBrowser(true);
      setBrowserType('Bing');
    }
    // YouTube App (com.google.ios.youtube / com.google.android.youtube)
    else if (ua.includes('com.google') && ua.includes('youtube')) {
      setIsInAppBrowser(true);
      setBrowserType('YouTube');
    }

    setIsDetectionComplete(true);
  }, []);

  return { isInAppBrowser, browserType, isDetectionComplete };
};
